'use client';

import React, { useState, useEffect, useMemo } from "react";
import SectionWrapper from "@/components/SectionWrapper";
import BlogCard from "@/components/cards/BlogCard";
import BlogCardSkeleton from "@/components/blog/BlogCardSkeleton";
import Filters from "@/components/blog/Filters";
import Pagination from "@/components/blog/Pagination";
import { FormattedBlogPost, formatBlogPost } from "@/lib/blog/blog-utils";
import Link from "next/link";
import { 
  useQuery, 
  keepPreviousData, 
  useQueryClient 
} from "@tanstack/react-query";

const PAGE_SIZE = 6;
const DEBOUNCE_DELAY = 500;

interface FilterOption {
  name: string;
  count: number;
}

interface BlogPageClientProps {
  // NEXT.JS 15 FIX: searchParams in the Client Component is a plain object 
  // but it's good to ensure these match what the server passes.
  searchParams: {
    q?: string;
    category?: string;
    tags?: string | string[];
    featured?: string;
  };
  dbCategories: FilterOption[];
  dbTags: FilterOption[];
}

interface BlogQueryResult {
  posts: FormattedBlogPost[];
  total: number;
  page: number;
}

type BlogQueryKey = [
  string,
  {
    page: number;
    category: string;
    q: string;
    tags: string[];
    featured: boolean;
  }
];

export default function BlogPageClient({ searchParams, dbCategories, dbTags }: BlogPageClientProps) {
  const queryClient = useQueryClient();

  // 1. Memoize initial values to prevent re-initialization on parent re-renders
  const initialFilters = useMemo(() => ({
    category: searchParams.category || "",
    q: searchParams.q || "",
    tags: Array.isArray(searchParams.tags) 
      ? searchParams.tags 
      : searchParams.tags ? [searchParams.tags] : [],
    featured: searchParams.featured === "true"
  }), [searchParams]);

  const [q, setQ] = useState(initialFilters.q);
  const [debouncedQ, setDebouncedQ] = useState(q);
  const [category, setCategory] = useState(initialFilters.category);
  const [tags, setTags] = useState<string[]>(initialFilters.tags);
  const [featured, setFeatured] = useState(initialFilters.featured);
  const [page, setPage] = useState(1);

  // 2. Debounce Logic
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedQ(q), DEBOUNCE_DELAY);
    return () => clearTimeout(handler);
  }, [q]);

  // 3. Reset to page 1 when any filter changes
  useEffect(() => {
    setPage(1);
  }, [debouncedQ, category, tags, featured]);

  const clearFilters = () => {
    setQ(""); 
    setCategory(""); 
    setTags([]); 
    setFeatured(false);
    setPage(1);
  };

  const toggleTag = (tag: string) => {
    setTags((prev) => (prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]));
  };

  // 4. Fetcher with strict Error Handling and Type Safety
  const fetchPosts = async ({ queryKey }: { queryKey: BlogQueryKey }): Promise<BlogQueryResult> => {
    const [_key, params] = queryKey;
    
    const urlParams = new URLSearchParams();
    urlParams.append("page", params.page.toString());
    urlParams.append("pageSize", PAGE_SIZE.toString());
    if (params.category) urlParams.append("category", params.category);
    if (params.q) urlParams.append("q", params.q);
    if (params.featured) urlParams.append("featured", "true");
    params.tags.forEach((t) => urlParams.append("tags", t));

    const res = await fetch(`/api/blog/posts?${urlParams.toString()}`);
    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(errorData.error || "Failed to sync with the central database");
    }

    const data = await res.json();
    
    // Ensure formatting logic handles potential missing data gracefully
    const mapped: FormattedBlogPost[] = (data.posts || []).map((post: any) => formatBlogPost(post));

    return { 
      posts: mapped, 
      total: data.total || 0, 
      page: data.page || 1 
    };
  };

  const queryKey: BlogQueryKey = ["blogPosts", { page, category, q: debouncedQ, tags, featured }];

  const { data, isLoading, isError, error, isPlaceholderData } = useQuery({
    queryKey,
    queryFn: fetchPosts,
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60 * 5, // 5 minutes cache
  });

  // 5. Intelligent Prefetching
  useEffect(() => {
    if (!isPlaceholderData && data?.total && page * PAGE_SIZE < data.total) {
      const nextPage = page + 1;
      const nextQueryKey: BlogQueryKey = ["blogPosts", { page: nextPage, category, q: debouncedQ, tags, featured }];

      queryClient.prefetchQuery({
        queryKey: nextQueryKey,
        queryFn: () => fetchPosts({ queryKey: nextQueryKey }),
        staleTime: 1000 * 60 * 5,
      });
    }
  }, [data, page, queryClient, category, debouncedQ, tags, featured, isPlaceholderData]);

  const totalPages = useMemo(() => Math.ceil((data?.total ?? 0) / PAGE_SIZE), [data]);

  return (
    <div className="min-h-screen py-12 bg-gradient-to-b from-white to-zinc-50 dark:from-zinc-900 dark:to-zinc-950">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Signal Filters */}
        <Filters
          q={q} setQ={setQ}
          category={category} setCategory={setCategory}
          categories={dbCategories} 
          tags={tags} toggleTag={toggleTag}
          allTags={dbTags}
          featured={featured} setFeatured={setFeatured}
          clearFilters={clearFilters}
        />

        {/* Error State */}
        {isError && (
          <div className="text-center py-16 animate-in fade-in zoom-in duration-300">
            <div className="inline-block p-6 bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800 rounded-3xl mb-6">
              <p className="text-red-600 dark:text-red-400 text-lg font-mono mb-2">ACCESS_DENIED: {error?.message}</p>
              <p className="text-red-500/70 text-sm italic">Possible uplink interruption or database timeout.</p>
            </div>
            <br />
            <button 
              onClick={() => window.location.reload()} 
              className="px-8 py-3 bg-red-600 text-white rounded-2xl font-black uppercase tracking-widest hover:bg-red-700 transition-all shadow-lg hover:shadow-red-600/20"
            >
              Restart Uplink
            </button>
          </div>
        )}

        {/* Results Counter */}
        {data && data.total > 0 && !isLoading && !isError && (
          <p className="text-center text-zinc-500 dark:text-zinc-400 mb-12 font-mono text-[10px] tracking-[0.3em] uppercase">
            Displaying Range: {(page - 1) * PAGE_SIZE + 1}—{Math.min(page * PAGE_SIZE, data.total)} / Total Transmissions: {data.total}
          </p>
        )}

        <div className="max-w-6xl mx-auto w-full">
          <div className="relative min-h-[20rem] lg:min-h-[400px]">
            {/* Loading Overlay for stale data updates */}
            {isPlaceholderData && (
              <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/40 dark:bg-zinc-950/40 backdrop-blur-[2px] rounded-2xl transition-all">
                <div className="bg-white dark:bg-zinc-800 p-4 rounded-xl shadow-xl border border-zinc-200 dark:border-zinc-700">
                  <div className="h-6 w-6 animate-spin rounded-full border-2 border-indigo-600 border-t-transparent"></div>
                </div>
              </div>
            )}

            {/* Main Content Grid */}
            <div className={`grid gap-6 sm:gap-8 grid-cols-1 md:grid-cols-2 lg:grid-cols-3 w-full transition-all duration-500 ${
              isPlaceholderData ? 'opacity-40 scale-[0.99] grayscale-[0.5]' : 'opacity-100 scale-100'
            }`}>
              {isLoading
                ? Array.from({ length: PAGE_SIZE }).map((_, idx) => (
                    <BlogCardSkeleton key={`skeleton-${idx}`} />
                  ))
                : data?.posts.map((post) => (
                    <BlogCard key={post.slug} post={post} />
                  ))}
            </div>
            
            {/* Empty State */}
            {!isLoading && !isError && data?.posts.length === 0 && (
              <div className="col-span-full text-center py-24 px-8 bg-zinc-50 dark:bg-zinc-900/30 rounded-3xl border-2 border-dashed border-zinc-200 dark:border-zinc-800 backdrop-blur-sm animate-in fade-in slide-in-from-bottom-4 duration-700">
                <div className="max-w-md mx-auto">
                  <p className="text-2xl font-black text-zinc-800 dark:text-zinc-200 mb-4 uppercase tracking-tighter italic">
                    Coordinates Unknown.
                  </p>
                  <p className="text-zinc-500 dark:text-zinc-400 mb-8 text-sm font-medium">
                    No transmissions found matching current parameters. Adjust the signal range or reset the arsenal filters.
                  </p>
                  <button 
                    onClick={clearFilters} 
                    className="px-8 py-3 bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 rounded-xl font-bold text-xs uppercase tracking-[0.2em] hover:bg-indigo-600 dark:hover:bg-indigo-600 dark:hover:text-white transition-all shadow-xl"
                  >
                    Reset All Signal Params
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Pagination Component */}
          {totalPages > 1 && !isLoading && !isError && data && (
            <div className="mt-20 flex justify-center">
              <Pagination 
                page={page} 
                setPage={setPage} 
                totalPages={totalPages} 
                isLoading={isLoading || isPlaceholderData} 
              />
            </div>
          )}
        </div>

        {/* Home Navigation */}
        <div className="mt-24 pt-20 border-t border-zinc-100 dark:border-zinc-800">
          <div className="text-center">
            <Link 
              href="/" 
              className="group inline-flex items-center gap-3 text-zinc-400 hover:text-indigo-600 transition-all font-mono text-[10px] uppercase tracking-[0.4em]"
            >
              <svg className="w-4 h-4 group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Return to Command Base
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}